import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const formatNumber = (value, digits = 1) => (
  value === null || value === undefined || Number.isNaN(Number(value)) ? "—" : Number(value).toFixed(digits)
);

const monoFamily = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** Small uppercase label above a value in the card. */
export function CardLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{ display: "block", color: "text.secondary", fontSize: 10, letterSpacing: "0.04em", lineHeight: 1.2 }}
    >
      {children}
    </Typography>
  );
}

/** A value under a CardLabel; ``mono`` for hex codes. */
export function CardValue({ children, mono = false }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.3,
        ...(mono ? { fontFamily: monoFamily, fontSize: "0.8rem" } : {}),
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * One palette entry of a reproduction as a card: the swatch with a button
 * that opens the color in the selection's gamut view, ``facts`` (label /
 * value pairs specific to the panel, e.g. the position in a blend or the
 * components assigned to it), then L*a*b*, the cluster width and the hex
 * code. ``children`` render below (e.g. the source colors mapped to it).
 * A ``missing`` entry (sample no longer in the palette) shows only its
 * facts and a note. The card states its values itself: no tooltip.
 * ``highlighted`` frames the card (the color of a component the user
 * clicked); ``cardRef`` reaches its box, e.g. to scroll it into view.
 * With ``onClick`` the whole card is a button (``aria-pressed`` mirrors
 * ``highlighted``); the gamut button does not trigger it.
 */
export default function PaletteColorCard({
  entry, facts = [], onOpenInGamut = null, testId = undefined, children = null, width = 188,
  highlighted = false, cardRef = null, onClick = null, ariaLabel = undefined,
}) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };
  return (
    <Box
      ref={cardRef}
      data-testid={testId}
      data-highlighted={highlighted ? "true" : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? highlighted : undefined}
      aria-label={onClick ? ariaLabel : undefined}
      onClick={onClick || undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      sx={{
        width, borderRadius: 2, overflow: "hidden",
        border: "1px solid", borderColor: highlighted ? "primary.main" : "divider",
        bgcolor: "background.paper",
        boxShadow: highlighted ? (theme) => `0 0 0 2px ${theme.palette.primary.main}` : "none",
        transition: "box-shadow 120ms, border-color 120ms",
        ...(onClick ? {
          cursor: "pointer",
          "&:hover": { borderColor: highlighted ? "primary.main" : "text.disabled" },
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
        } : {}),
      }}
    >
      <Box sx={{ position: "relative", height: 64, bgcolor: entry.hex || "transparent" }}>
        {!entry.missing && onOpenInGamut && (
          <Tooltip title="Show this color in the selection's gamut">
            <IconButton
              size="small"
              aria-label={`Open color ${entry.index} in the selection gamut`}
              onClick={(event) => { event.stopPropagation(); onOpenInGamut(entry.sample_id); }}
              sx={{
                position: "absolute", top: 6, right: 6, width: 26, height: 26,
                bgcolor: "rgba(255,255,255,0.92)", color: "text.primary",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.25)",
                "&:hover": { bgcolor: "#ffffff" },
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ px: 1.25, pt: 1, pb: 1.25 }}>
        {facts.length > 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${facts.length}, 1fr)`, gap: 0.5 }}>
            {facts.map((fact) => (
              <Box key={fact.label}>
                <CardLabel>{fact.label}</CardLabel>
                <CardValue>{fact.value}</CardValue>
              </Box>
            ))}
          </Box>
        )}
        {entry.missing ? (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
            Not in the palette any more
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.5,
                mt: facts.length ? 0.75 : 0, pt: facts.length ? 0.75 : 0,
                borderTop: facts.length ? "1px solid" : "none", borderColor: "divider",
              }}
            >
              {[["L*", entry.l_star], ["a*", entry.a_star], ["b*", entry.b_star]].map(([name, number]) => (
                <Box key={name}>
                  <CardLabel>{name}</CardLabel>
                  <CardValue>{formatNumber(number)}</CardValue>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mt: 0.75 }}>
              <Box>
                <CardLabel>WIDTH</CardLabel>
                <CardValue>
                  {entry.width_mm != null && Number(entry.width_mm) > 0
                    ? `${(Number(entry.width_mm) * 1000).toFixed(1)} µm`
                    : "—"}
                </CardValue>
              </Box>
              <Box>
                <CardLabel>HEX</CardLabel>
                <CardValue mono>{String(entry.hex || "").toUpperCase()}</CardValue>
              </Box>
            </Box>
            {children}
          </>
        )}
      </Box>
    </Box>
  );
}
